import { DOCUMENT_STATES, ROLES } from '../data/users';
import { authService } from './authService';

// ─── Caché en memoria con TTL de 5 minutos ────────────────────────────────────
const CACHE_TTL = 5 * 60 * 1000;
let workordersCache = null;
let workordersCacheTime = 0;

// ─── Persistencia de tareas completadas en localStorage ──────────────────────
const COMPLETED_KEY = 'zapata_tasks_completed';

const getCompletedIds = () => {
  try {
    return JSON.parse(localStorage.getItem(COMPLETED_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveCompletedId = (taskId) => {
  const ids = getCompletedIds();
  if (!ids.includes(taskId)) {
    ids.push(taskId);
    localStorage.setItem(COMPLETED_KEY, JSON.stringify(ids));
  }
};

const removeCompletedId = (taskId) => {
  const ids = getCompletedIds().filter((id) => id !== taskId);
  localStorage.setItem(COMPLETED_KEY, JSON.stringify(ids));
};

// ─────────────────────────────────────────────────────────────────────────────

export const tasksService = {
  fetchWorkorders: async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && workordersCache && now - workordersCacheTime < CACHE_TTL) {
      return workordersCache;
    }

    try {
      const response = await fetch('/.netlify/functions/getStelOrderData', { headers: { ...authService.authHeader() } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      workordersCache = data.workorders || [];
      workordersCacheTime = Date.now();
      return workordersCache;
    } catch (error) {
      console.error('Error fetching workorders:', error);
      return workordersCache || [];
    }
  },

  getYesterdayDate: () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    let daysToSubtract = 1;
    if (dayOfWeek === 1) daysToSubtract = 3; // lunes → viernes
    else if (dayOfWeek === 0) daysToSubtract = 2; // domingo → viernes

    const lastWorkday = new Date();
    lastWorkday.setDate(lastWorkday.getDate() - daysToSubtract);

    // Use local date components to match getStelOrderData.js normalizeFecha()
    const year = lastWorkday.getFullYear();
    const month = String(lastWorkday.getMonth() + 1).padStart(2, '0');
    const day = String(lastWorkday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  processWorkordersToTasks: (workorders) => {
    // Detect the most recent past date in workorders — handles weekends and holidays automatically
    const today = new Date();
    const todayStr = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0'),
    ].join('-');

    const pastDates = [...new Set(workorders.map((wo) => wo.date).filter((d) => d && d < todayStr))].sort();
    const yesterdayDate = pastDates.length > 0 ? pastDates[pastDates.length - 1] : tasksService.getYesterdayDate();

    const completedIds = getCompletedIds();
    const tasks = [];

    const yesterdayWorkorders = workorders.filter(
      (wo) => wo.date === yesterdayDate && wo['document-state-id']
    );

    yesterdayWorkorders.forEach((wo) => {
      const stateId = wo['document-state-id'];
      const stateInfo = DOCUMENT_STATES[stateId];

      if (!stateInfo || !stateInfo.role || !stateInfo.task) return;
      if (stateInfo.name === 'Cerrado') return;

      const taskId = `task-${wo.id}`;
      if (completedIds.includes(taskId)) return;

      tasks.push({
        id: taskId,
        workorderId: wo.id,
        title: `${stateInfo.name} - ${wo['full-reference'] || wo['customer-name'] || `WO #${wo.id}`}`,
        description: stateInfo.task,
        assignedRole: stateInfo.role,
        priority: tasksService.getPriority(stateInfo.name),
        status: 'pending',
        createdAt: new Date().toISOString(),
        workorderData: {
          id: wo.id,
          date: wo.date,
          state: stateInfo.name,
          stateId,
          customerName: wo['customer-name'],
          customerPhone: wo['customer-phone'],
          customerEmail: wo['customer-email'],
          customerAddress: wo['customer-address'],
          assigneeName: wo['assignee-name'],
          agentName: wo['agent-name'],
          fullReference: wo['full-reference'],
          description: wo.description,
          comments: wo.comments,
          privateComments: wo['private-comments'],
        },
      });
    });

    return { tasks, date: yesterdayDate };
  },

  getPriority: (stateName) => {
    const high = ['Pendiente', 'Ausente', 'Pendiente de pago', 'Pendiente de material', 'Pendiente de presupuesto', 'Pendiente de sustitución'];
    const medium = ['PRE LIQUIDACION ARISTON'];
    if (high.includes(stateName)) return 'high';
    if (medium.includes(stateName)) return 'medium';
    return 'low';
  },

  filterTasksByRole: (tasks, userRole) => {
    if (userRole === ROLES.ADMIN) return tasks;
    return tasks.filter((task) => task.assignedRole === userRole);
  },

  getTasksForUser: async (userRole, forceRefresh = false) => {
    try {
      const workorders = await tasksService.fetchWorkorders(forceRefresh);
      const { tasks: allTasks, date: tasksDate } = tasksService.processWorkordersToTasks(workorders);
      const userTasks = tasksService.filterTasksByRole(allTasks, userRole);

      return {
        success: true,
        tasks: userTasks,
        date: tasksDate,
        total: userTasks.length,
        byPriority: {
          high: userTasks.filter((t) => t.priority === 'high').length,
          medium: userTasks.filter((t) => t.priority === 'medium').length,
          low: userTasks.filter((t) => t.priority === 'low').length,
        },
      };
    } catch (error) {
      return { success: false, error: error.message, tasks: [], total: 0 };
    }
  },

  markTaskAsCompleted: async (taskId) => {
    saveCompletedId(taskId);
    return { success: true };
  },

  undoTaskCompleted: (taskId) => {
    removeCompletedId(taskId);
  },

  getTasksStats: (tasks) => {
    const stats = {
      total: tasks.length,
      byRole: {},
      byPriority: {
        high: tasks.filter((t) => t.priority === 'high').length,
        medium: tasks.filter((t) => t.priority === 'medium').length,
        low: tasks.filter((t) => t.priority === 'low').length,
      },
      byStatus: {
        pending: tasks.filter((t) => t.status === 'pending').length,
        completed: tasks.filter((t) => t.status === 'completed').length,
      },
    };

    tasks.forEach((task) => {
      if (!stats.byRole[task.assignedRole]) stats.byRole[task.assignedRole] = 0;
      stats.byRole[task.assignedRole]++;
    });

    return stats;
  },
};
